import { useCallback, useEffect } from 'react';

type MessageMode = 'emit' | 'subscribe';

interface PostMessagePayload<T = unknown> {
    event: string;
    data?: T;
}

export function usePostMessage<T = unknown>(
    eventName: string,
    mode: MessageMode,
    callback?: (data: T) => void,
) {
    useEffect(() => {
        if (mode !== 'subscribe' || !callback) {
            return;
        }

        const handler = (event: MessageEvent<PostMessagePayload<T>>) => {
            if (event.data?.event !== eventName) {
                return;
            }

            callback(event.data.data as T);
        };

        window.addEventListener('message', handler);

        return () => {
            window.removeEventListener('message', handler);
        };
    }, [ eventName, mode, callback ]);

    return useCallback(
        (iframeId: string, data?: T) => {
            if (mode !== 'emit') {
                return;
            }

            const iframe = document.getElementById(
                iframeId,
            ) as HTMLIFrameElement | null;

            iframe?.contentWindow?.postMessage(
                {
                    event: eventName,
                    data,
                },
                '*',
            );
        },
        [ eventName, mode ],
    );
}
